import { useTranslation } from 'next-i18next';
import { ISupplier } from '@bridebook/models/source/models/Suppliers.types';
import { ISupplierAdmin } from '@bridebook/models/source/models/Suppliers/Admin.types';
import type { IOffer } from '@bridebook/models/source/models/Suppliers/Offers.types';
import { Box, Well } from '@bridebook/ui';
import { IconReview } from '@bridebook/ui/src/icons';
import { formatBbGift, isDefined } from '@bridebook/web/app-shared/lib/supplier/formatters';
import getBBDiscount from '@bridebook/web/app-shared/lib/supplier/utils/get-bb-discount';
import hasBenefits from '@bridebook/web/app-shared/lib/supplier/utils/has-benefits';
import componentStyles from '@bridebook/web/app-basic/components/supplier/sections/benefits/benefits.style';
import { compact } from 'lodash';

interface IProps {
  supplier: ISupplier;
  premium: ISupplierAdmin['premium'] | null;
  offers: IOffer[] | null | undefined;
}

const Benefits = ({ supplier, premium, offers }: IProps) => {
  const { t } = useTranslation('supplier');

  const isBenefits = hasBenefits({ supplier, offers, premium: premium || undefined });
  if (!isBenefits) return null;
  const specialOffer = (supplier as ISupplier).bridebook?.offer;

  const discount = getBBDiscount(offers);
  const gift = formatBbGift(
    (supplier as ISupplier).bridebook?.gift,
    (supplier as ISupplier).bridebook?.giftOther,
  );

  const items = compact([
    !premium
      ? {
          name: 'discount',
          content: {
            text: t('supplier.section.benefits.item.discount'),
            value: discount,
          },
        }
      : undefined,
    {
      name: 'gift',
      content: {
        text: t('supplier.section.benefits.item.gift'),
        value: gift,
      },
    },
    {
      name: 'offer',
      content: {
        text: t('supplier.section.benefits.item.specialOffer'),
        value: specialOffer,
      },
    },
  ]);

  const styles = componentStyles();

  return (
    <Box p={4}>
      <Well
        size="full"
        icon={
          <Box style={styles.iconWell}>
            <IconReview width={10} />
          </Box>
        }
        heading={t('supplier.section.benefits.well.heading')}>
        {items.map(
          (item) =>
            item &&
            isDefined(item.content.value) && (
              <Box style={styles.item} key={`benefits-block-item-${item.name}`}>
                {item.content.text}
                <Box as="span" style={styles.value}>
                  {item.content.value}
                </Box>
              </Box>
            ),
        )}
      </Well>
    </Box>
  );
};

export default Benefits;
